import { useQuery } from "@tanstack/react-query";

import { formatLenderResponseFormData } from "@/components/lender-application-details/bids/bidHelpers";
import { useGetLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { queryKeyMap } from "@/features/query-client";

import { getOfferAttractiveness } from "../../api";
import type { ILenderResponseInput } from "../../types/lender-response";

interface IUseGetOfferAttractivenessVariables {
  lenderCompanyId: number;
  lenderApplicationId: number;
  input: ILenderResponseInput;
}

interface IUseGetOfferAttractivenessOptions {
  enabled: boolean;
}

export const useGetOfferAttractiveness = (
  {
    lenderCompanyId,
    lenderApplicationId,
    input,
  }: IUseGetOfferAttractivenessVariables,
  options: IUseGetOfferAttractivenessOptions,
) => {
  const { data: lenderCompany } = useGetLenderCompanyQuery(lenderCompanyId, {
    enabled: !!lenderCompanyId,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const isFeatureInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const inputWithoutFile = formatLenderResponseFormData(
    {
      ...input,
      creditTermsFile: undefined,
    },
    "TO_MONTHLY",
    isFeatureInterestRatePerYearActivated,
  );

  return useQuery({
    queryKey: queryKeyMap.getOfferAttractiveness(
      lenderCompanyId,
      lenderApplicationId,
      inputWithoutFile,
    ),
    queryFn: async () => {
      return await getOfferAttractiveness(
        lenderCompanyId,
        lenderApplicationId,
        inputWithoutFile,
      );
    },
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    ...options,
  });
};
